import * as React from 'react';

import Group from '@lightspeed/cirrus-group';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { IconDownload } from '@lightspeed/flame/Icon/Download';
import { Spinner } from '@lightspeed/flame/Spinner';
import PropTypes from 'prop-types';

import LabelPrintModal from '../LabelPrintModal';
import CheckAgain from './CheckAgain';
import { DeprecationNotice } from './DeprecationNotice';

/**
 * Supported operating systems
 */
const operatingSystems = {
  MAC: 'Mac',
  WINDOWS: 'Windows',
};

/**
 * The filename to be downloaded
 * Update these constants as new version gets released
 */
const filename = {
  MAC: 'BrowserPrint-1.2.0.3-2.dmg',
  WINDOWS: 'BrowserPrint-1.2.0.exe',
};

/**
 * Class Install displays download link in case the Mac / Windows Zebra app is not installed
 */
class Install extends React.Component {
  /**
   * Construct the Install Modal
   * @param {Object} props - Component properties
   */
  constructor(props) {
    super(props);

    this.state = {
      isCheckAgainVisible: false,
    };

    this.operatingSystem = null;
    this.downloadUrl = null;

    if (~navigator.platform.indexOf('Mac')) {
      this.operatingSystem = operatingSystems.MAC;
    } else if (~navigator.platform.indexOf('Win')) {
      this.operatingSystem = operatingSystems.WINDOWS;
    }

    if (this.operatingSystem) {
      this.downloadUrl = this.getDownloadUrl();
    } else {
      this.props.setStep(LabelPrintModal.steps.ERROR, { error: 'GENERIC' });
    }

    this.close = this.close.bind(this);
    this.handleCheckAgainClick = this.handleCheckAgainClick.bind(this);
    this.handleDownloadOnClick = this.handleDownloadOnClick.bind(this);
  }

  /**
   * Provide the App download url depending on client's OS
   * @returns {string} download url
   */
  getDownloadUrl() {
    let downloadUrl = LabelPrintModal.links.download;

    switch (this.operatingSystem) {
      case operatingSystems.MAC:
        downloadUrl += filename.MAC;
        break;
      case operatingSystems.WINDOWS:
      default:
        downloadUrl += filename.WINDOWS;
        break;
    }

    return downloadUrl;
  }

  /**
   * Toggle the visibility of the CheckAgain text
   */
  handleCheckAgainClick() {
    this.setState({
      isCheckAgainVisible: true,
    });
  }

  /**
   * Implement additional functionality to the onHide parent function
   */
  close() {
    this.props.onHide();
  }

  handleDownloadOnClick() {
    this.props.setStep(LabelPrintModal.steps.INSTALLING);
  }

  /**
   * Render the Install modal
   * @returns {Object} DOM Element
   */
  render() {
    return (
      <Modal
        className={`label-print-modal ${this.props.currentStep.toLowerCase()} cirrus responsive`}
        isOpen={this.props.show}
        onRequestClose={this.close}
      >
        <ModalHeader onCloseClicked={this.close}>Zebra Browser Print</ModalHeader>
        <ModalBody>
          <div className="cr-pt-4 cr-mb-4">
            <DeprecationNotice callback={this.close} />
            <div className="cr-flex cr-width-full cr-items-center cr-mb-4">
              <div className="cr-inline-flex cr-mr-4">
                <img
                  className="medal-printer"
                  src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/label/medal-printer.svg"
                  alt="install printer"
                />
              </div>
              <div>
                <h4 className="cr-text cr-bold cr-mb-0">Waiting for Zebra Browser Print</h4>
                <p className="cr-text-s cr-mt-1 cr-mb-4">
                  Make sure Zebra Browser Print is running and try printing again.
                </p>
                <p className="cr-text-s">
                  Is Zebra Browser Print already running?{' '}
                  <a href={null} onClick={this.handleCheckAgainClick}>
                    Check Again
                  </a>
                </p>
              </div>
            </div>
            {this.state.isCheckAgainVisible && <CheckAgain />}
          </div>
          <div style={{ margin: '0 -1.5rem' }}>
            <Divider />
          </div>
          <Group className="cr-pv-2">
            <span className="cr-text-s cr-mr-3">First time seeing this?</span>
            <a href={this.downloadUrl} download>
              <Button variant="primary" type="submit" onClick={this.handleDownloadOnClick}>
                <IconDownload />
                Install Zebra Browser Print
              </Button>
            </a>
          </Group>
        </ModalBody>
        <ModalFooter>
          <div className="cr-flex cr-justify-between">
            <a href={LabelPrintModal.links.documentation} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">Help</Button>
            </a>
            <Group className="cr-justify-end">
              <Button onClick={this.close}>Cancel</Button>
              <Button variant="primary" disabled onClick={this.handleCheckAgainClick}>
                <Spinner />
                Waiting for Zebra Browser Print
              </Button>
            </Group>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

Install.propTypes = {
  setStep: PropTypes.func.isRequired,
  currentStep: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default Install;
